import React, { useState } from "react"

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import MemberRecord from "../molecules/MemberRecord"
import Pagination from "./Pagination"
import SortableHeader, { SortDirection } from "./SortableHeader"
import { MemberDetailsModal, NewMessageModal } from "../molecules/AppModal"
import { MembershipSchema } from "../data/types";
import { MemberSortField } from "../utils/helpers";

export type { MemberSortField, SortDirection };

type MembersListProps = {
  kitchen_capacity: number
  members: MembershipSchema[],
  isRequests?: boolean,
  currentPage: number,
  currentMembers: MembershipSchema[],
  totalPages: number,
  itemsPerPage: number,
  handlePageChange: (page: number) => void,
  sortField: MemberSortField
  sortDirection: SortDirection
  onSort: (field: MemberSortField) => void
  hasSearchFilter?: boolean
}

const MEMBER_HEADER_CELL = 'px-3 py-2 text-left text-xs font-medium tracking-wider text-gray-500 uppercase'

const MembersList: React.FC<MembersListProps> = ({
  kitchen_capacity,
  members,
  isRequests,
  currentPage,
  currentMembers,
  totalPages,
  itemsPerPage,
  handlePageChange,
  sortField,
  sortDirection,
  onSort,
  hasSearchFilter,
}) => {
  const [allselected, updateAllSelected] = useState(false)
  const [viewSendMessage, toggleViewSendMessage] = useState(false);
  const [viewMemberDetails, toggleViewMemberDetails] = useState(false);
  const [selectedMember, setSelectedMember] = useState<MembershipSchema | null>(null);

  const handleViewMemberDetails = (member: MembershipSchema) => {
    setSelectedMember(member);
    toggleViewMemberDetails(true);
  }

  const handleViewSendMessage = (member: MembershipSchema) => {
    setSelectedMember(member);
    toggleViewSendMessage(true);
  }

  const emptyMessage = hasSearchFilter ? 'No matching members' : 'No Members'

  return (
    <div data-testid="members-list" className="w-full min-w-0 overflow-x-auto">
      <table className="min-w-full w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className={MEMBER_HEADER_CELL}>
              {allselected ? (
                <CheckBoxIcon
                  onClick={() => updateAllSelected(false)}
                  className="cursor-pointer"
                />
              ) : (
                <CheckBoxOutlineBlankIcon
                  onClick={() => updateAllSelected(true)}
                  className="cursor-pointer"
                />
              )}
              <KeyboardArrowDownIcon className="ml-1" style={{ fontSize: 15 }} />
            </th>

            <SortableHeader
              label="Full Name"
              field="full_name"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={onSort}
              cellClassName={MEMBER_HEADER_CELL}
            />

            <SortableHeader
              label="Username"
              field="username"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={onSort}
              cellClassName={MEMBER_HEADER_CELL}
            />

            <SortableHeader
              label="Team"
              field="team_name"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={onSort}
              cellClassName={MEMBER_HEADER_CELL}
            />

            <SortableHeader
              label="Membership Status"
              field="status"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={onSort}
              cellClassName={MEMBER_HEADER_CELL}
            />

            <SortableHeader
              label="Membership Tier"
              field="plan_name"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={onSort}
              cellClassName={MEMBER_HEADER_CELL}
            />

            <SortableHeader
              label="Member Balance"
              field="member_balance"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={onSort}
              cellClassName={MEMBER_HEADER_CELL}
            />

            <SortableHeader
              label="Joined"
              field="joined"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={onSort}
              cellClassName={MEMBER_HEADER_CELL}
            />

            <th scope="col" className={MEMBER_HEADER_CELL}>
              {isRequests ? "" : "Actions"}
            </th>
          </tr>
        </thead>

        <tbody className="bg-white divide-y divide-gray-200">
          {currentMembers?.length ? (
            currentMembers.map((member: MembershipSchema, i: number) => (
              <MemberRecord
                key={member.id ?? i}
                member={member}
                allSelected={allselected}
                isRequests={isRequests}
                onViewMemberDetails={() => handleViewMemberDetails(member)}
                onViewSendMessage={() => handleViewSendMessage(member)}
              />
            ))
          ) : (
            <tr>
              <td colSpan={9} className="px-3 py-2 text-sm text-gray-500 text-center">
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      
      {members.length > itemsPerPage && (
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={members.length}
            itemsPerPage={itemsPerPage}
            showInfo={true}
            itemLabel="Members"
            showFirstLast={true}
            maxVisiblePages={5}
          />
        </div>
      )}

      {selectedMember && (
        <>
          <MemberDetailsModal 
            isOpen={viewMemberDetails} 
            closeAppModal={() => toggleViewMemberDetails(false)} 
            member={selectedMember} 
          />
          <NewMessageModal 
            username={selectedMember?.username} 
            isOpen={viewSendMessage} 
            closeAppModal={() => toggleViewSendMessage(false)} 
          />
        </>
      )}
    </div>
  );
};

export default MembersList;
